import { Box, Choicebox, FormGroup } from '@ksyos/design-system';
import type { OsasTrialPeriod } from '@common/enums';
import type {
    OsasEvaluationData,
    TreatmentChoice,
} from '@common/validation/customControls/osas/osasEvaluationSchemas/types';
import { AssetCategory } from '@common/enums';
import { never } from '@common/utils/never';
import { getSelectedLanguage } from '@frontend/translate';
import { useProcessActivityEditContext } from '@frontend/utils';
import {
    initialLifeStyleTreatmentData,
    initialMRATreatmentData,
    initialPAPTreatmentData,
    initialSPTTreatmentData,
} from '../osasEvaluationData';
import { LifestyleFormSections } from './LifestyleFormSections';
import { MRAFormSections } from './MRAFormSections';
import { PAPFormSections } from './PAPFormSections';
import { SPTFormSections } from './SPTFormSections';

type TreatmentChoiceFormSectionProps = {
    hasTreatmentSections: boolean;
    onChangeTrialPeriod: (reatmentsToChange: TreatmentChoice, trialPeriod: OsasTrialPeriod) => void;
};

export function TreatmentChoiceFormSection(props: Readonly<TreatmentChoiceFormSectionProps>) {
    const { hasTreatmentSections, onChangeTrialPeriod } = props;
    const { data, onChange } = useProcessActivityEditContext<OsasEvaluationData>();
    const translate = getSelectedLanguage();

    const treatmentChoice: (keyof TreatmentChoice)[] = ['SPT', 'MRA', 'PAP', 'lifestyle'];

    return (
        <>
            <FormGroup label="Behandeling">
                <Box
                    display="flex"
                    flexWrap="wrap"
                    gap={3}
                >
                    {treatmentChoice.map((treatment) => (
                        <Choicebox
                            checked={data.treatmentChoice[treatment] ?? undefined}
                            key={`treatmentChoice.${treatment}`}
                            label={translate.osas[treatment]}
                            onChange={(e) => {
                                const updatedTreatmentChoice = {
                                    ...data.treatmentChoice,
                                    [treatment]: e.target.checked,
                                };
                                onChange(
                                    {
                                        treatmentChoice: updatedTreatmentChoice,
                                        ...(!e.target.checked && {
                                            ...getInitialData(treatment),
                                        }),
                                    },
                                    'treatmentChoice'
                                );
                                if (data.trialPeriod !== null) {
                                    onChangeTrialPeriod(updatedTreatmentChoice, data.trialPeriod);
                                }
                            }}
                        />
                    ))}
                </Box>
            </FormGroup>

            {hasTreatmentSections && data.treatmentChoice.MRA && <MRAFormSections />}
            {hasTreatmentSections && data.treatmentChoice.PAP && <PAPFormSections />}
            {hasTreatmentSections && data.treatmentChoice.SPT && <SPTFormSections />}
            {hasTreatmentSections && data.treatmentChoice.lifestyle && <LifestyleFormSections />}
        </>
    );

    function getInitialData(treatment: keyof OsasEvaluationData['treatmentChoice']) {
        if (!hasTreatmentSections) {
            return;
        }

        switch (treatment) {
            case 'MRA':
                return {
                    ...initialMRATreatmentData,
                    assets: data.assets.filter((asset) => asset.category !== AssetCategory.mraAttachment),
                };
            case 'SPT':
                return {
                    ...initialSPTTreatmentData,
                    assets: data.assets.filter((asset) => asset.category !== AssetCategory.sptReport),
                };
            case 'PAP':
                return {
                    ...initialPAPTreatmentData,
                    assets: data.assets.filter((asset) => asset.category !== AssetCategory.papReport),
                };
            case 'lifestyle':
                return {
                    ...initialLifeStyleTreatmentData,
                    assets: data.assets.filter((asset) => asset.category !== AssetCategory.lifestyleAttachment),
                };
            default:
                never(treatment);
        }
    }
}
